<script>
import { getProductAccountList, getProductList, getSellerList, registerCurrentBranchProduct, toggleProductView, updateProductSellers } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getBrandList } from '@/api/brand.js'
import { getAdminInfo } from '@/utils/token.js'
import ProductThumbnail from './ProductThumbnail.vue'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'ProductListPage',
  components: { ProductThumbnail, AppPagination },

  data() {
    return {
      search: {
        keyword: '',
        categoryCode: '',
        brandCode: '',
        sellerCode: '',
        viewYn: '',
      },
      categoryOptions: [{ title: '전체', value: '' }],
      brandOptions: [{ title: '전체', value: '' }],
      // 공급사(판매자는 아우라, 골드나라는 '공급사' - 2026-11-01 결정). NONE = 아직 지정하지 않은 상품
      sellers: [],
      selectedCodes: [],
      bulkSellerCode: '',
      bulkSaving: false,
      viewYnOptions: [
        { title: '전체', value: '' },
        { title: '노출', value: 'Y' },
        { title: '미노출', value: 'N' },
      ],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      togglingSet: new Set(),
      adminInfo: {},
      branchProductTab: 'REGISTERED',
      registerDialog: false,
      registerTarget: null,
      registerForm: {
        processingFee: 0,
        branchAccountCode: '',
      },
      cashAccountOptions: [{ title: '선택 안함', value: '' }],
      registering: false,
    }
  },

  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },

    sellerFilterOptions() {
      return [
        { title: '전체', value: '' },
        { title: '미지정', value: 'NONE' },
        ...this.sellers.map(s => ({ title: s.sellerName, value: s.sellerCode })),
      ]
    },

    sellerChoiceOptions() {
      return this.sellers.map(s => ({ title: s.sellerName, value: s.sellerCode }))
    },

    allPageSelected() {
      return this.items.length > 0 && this.items.every(item => this.selectedCodes.includes(item.productCode))
    },

    columnCount() {
      return this.isHq ? 17 : 16
    },

    registerDialogTitle() {
      return this.registerTarget?.branchProductCode ? '지점 상품 설정 수정' : '지점 상품 설정 등록'
    },

    registerDialogActionText() {
      return this.registerTarget?.branchProductCode ? '저장' : '등록'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    Promise.all([this.fetchCategories(), this.fetchBrands(), this.fetchSellers(), this.fetchCashAccounts(), this.fetchList()])
  },

  methods: {
    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = [...(res.data.data?.items || [])].sort(this.sortByOrder)
          this.categoryOptions = [
            { title: '전체', value: '' },
            ...list.map(item => ({ title: item.categoryName, value: item.categoryCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductListPage] fetchCategories error:', error)
      }
    },

    async fetchBrands() {
      try {
        const res = await getBrandList({ page: 1, size: 100, activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.brandOptions = [
            { title: '전체', value: '' },
            ...(res.data.data?.items || []).map(item => ({ title: item.brandName, value: item.brandCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductListPage] fetchBrands error:', error)
      }
    },

    async fetchSellers() {
      try {
        const res = await getSellerList()
        if (res.data?.code === 0) this.sellers = res.data.items || []
      } catch (error) {
        console.error('[ProductListPage] fetchSellers error:', error)
      }
    },

    isSelected(item) {
      return this.selectedCodes.includes(item.productCode)
    },

    toggleSelect(item) {
      this.selectedCodes = this.isSelected(item)
        ? this.selectedCodes.filter(code => code !== item.productCode)
        : [...this.selectedCodes, item.productCode]
    },

    toggleSelectAll() {
      const pageCodes = this.items.map(item => item.productCode)
      this.selectedCodes = this.allPageSelected
        ? this.selectedCodes.filter(code => !pageCodes.includes(code))
        : [...new Set([...this.selectedCodes, ...pageCodes])]
    },

    async applyBulkSeller() {
      if (!this.selectedCodes.length || !this.bulkSellerCode || this.bulkSaving) return
      const seller = this.sellers.find(s => s.sellerCode === this.bulkSellerCode)
      if (!confirm(`선택한 ${this.selectedCodes.length}개 상품의 공급사를 '${seller?.sellerName || ''}'(으)로 바꿀까요?`)) return
      this.bulkSaving = true
      try {
        const res = await updateProductSellers({ productCodes: this.selectedCodes, sellerCode: this.bulkSellerCode })
        if (res.data?.code === 0) {
          alert(res.data.message || '공급사를 바꿨습니다.')
          this.selectedCodes = []
          await this.fetchList()
          return
        }
        alert(res.data?.message || '공급사를 바꾸지 못했습니다.')
      } catch (error) {
        console.error('[ProductListPage] bulk seller error:', error)
        alert(error?.response?.data?.message || '공급사를 바꾸지 못했습니다.')
      } finally {
        this.bulkSaving = false
      }
    },

    async fetchCashAccounts() {
      if (this.isHq) return
      try {
        const res = await getProductAccountList({ page: 1, size: 100, activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.cashAccountOptions = [
            { title: '선택 안함 - 본사 기준 계좌 사용', value: '' },
            ...(res.data.data?.items || []).map(item => ({
              title: this.accountOptionText(item),
              value: item.branchAccountCode,
            })),
          ]
        }
      } catch (error) {
        console.error('[ProductListPage] fetchCashAccounts error:', error)
      }
    },

    async fetchList() {
      this.loading = true
      try {
        const res = await getProductList({
          keyword: this.search.keyword || undefined,
          categoryCode: this.search.categoryCode || undefined,
          brandCode: this.search.brandCode || undefined,
          sellerCode: this.search.sellerCode || undefined,
          viewYn: this.search.viewYn || undefined,
          includeBranchProducts: this.isHq ? undefined : 'Y',
          branchRegistered: this.isHq ? undefined : (this.branchProductTab === 'REGISTERED' ? 'Y' : 'N'),
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = [...(res.data.data?.items || [])].sort(this.sortByOrder)
          this.total = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('[ProductListPage] fetchList error:', error)
      } finally {
        this.loading = false
      }
    },

    sortByOrder(a, b) {
      const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
      const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
    },

    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    handleReset() {
      this.search = { keyword: '', categoryCode: '', brandCode: '', sellerCode: '', viewYn: '' }
      this.page = 1
      this.fetchList()
    },

    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    handleBranchTabChange() {
      this.page = 1
      this.fetchList()
    },

    goToDetail(item) {
      if (!this.isHq) {
        this.openRegisterDialog(item)
        return
      }
      this.$router.push(`/products/${item.productCode}`)
    },

    goToCreate() {
      this.$router.push('/products/create')
    },

    async handleToggleView(item) {
      if (this.togglingSet.has(item.productCode)) return
      this.togglingSet = new Set([...this.togglingSet, item.productCode])
      try {
        const res = await toggleProductView(item.productCode)
        if (res.data?.code === 0) {
          const index = this.items.findIndex(row => row.productCode === item.productCode)
          if (index >= 0) {
            this.items.splice(index, 1, {
              ...this.items[index],
              viewYn: res.data.viewYn,
              productStatus: res.data.viewYn === 'Y' ? 'ON_SALE' : 'HIDDEN',
            })
          }
        }
      } catch (error) {
        console.error('[ProductListPage] toggleView error:', error)
      } finally {
        const next = new Set(this.togglingSet)
        next.delete(item.productCode)
        this.togglingSet = next
      }
    },

    openRegisterDialog(item) {
      this.registerTarget = item
      this.registerForm = {
        processingFee: item.conventionCost ?? 0,
        branchAccountCode: item.branchAccountCode || '',
      }
      this.registerDialog = true
    },

    async handleRegisterBranchProduct() {
      if (!this.registerTarget || this.registering) return
      this.registering = true
      try {
        const res = await registerCurrentBranchProduct(this.registerTarget.productCode, {
          stockQty: null,
          processingFee: this.registerForm.processingFee,
          branchAccountCode: this.registerForm.branchAccountCode || null,
        })
        if (res.data?.code === 0) {
          this.registerDialog = false
          this.registerTarget = null
          await this.fetchList()
          return
        }
        alert(res.data?.message || '지점 상품 설정 등록에 실패했습니다.')
      } catch (error) {
        console.error('[ProductListPage] register branch product error:', error)
        alert('지점 상품 설정 등록 중 오류가 발생했습니다.')
      } finally {
        this.registering = false
      }
    },

    formatDate(value) {
      return value ? value.substring(0, 10) : '-'
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      return Number.isNaN(number) ? '-' : number.toLocaleString('ko-KR')
    },

    accountOptionText(item) {
      const branch = item.branchName || '현재 지점'
      const bank = item.bankName || '-'
      const accountNo = item.accountNo || '-'
      const holder = item.accountHolder || '-'
      return `${branch} · ${bank} ${accountNo} · ${holder}`
    },

    statusText(value) {
      return { ON_SALE: '판매중', HIDDEN: '숨김', ENDED: '판매종료' }[value] || '-'
    },

    stockText(value) {
      return { IN_STOCK: '재고있음', OUT_OF_STOCK: '품절', COMING_SOON: '입고예정', STOPPED: '판매중지' }[value] || '-'
    },

    discountText(item) {
      if (item.discountLabel) return item.discountLabel
      if (item.discountType === 'PRODUCT_PRICE' && item.discountRate) return `${item.discountRate}%`
      if (item.discountType === 'MARGIN' && item.discountAmount) return `${this.formatMoney(item.discountAmount)}원`
      return '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">상품 관리</div>
        <div class="text-body-2 text-grey mt-1">상품 조회 및 등록</div>
      </div>
      <v-btn v-if="isHq" color="primary" prepend-icon="mdi-plus" @click="goToCreate">
        상품 등록
      </v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <div class="search-row">
          <v-text-field
            v-model="search.keyword"
            label="상품 검색"
            placeholder="상품명"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="handleSearch"
          />
          <v-select
            v-model="search.categoryCode"
            :items="categoryOptions"
            label="카테고리"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-model="search.brandCode"
            :items="brandOptions"
            label="브랜드"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-model="search.sellerCode"
            :items="sellerFilterOptions"
            label="공급사"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-model="search.viewYn"
            :items="viewYnOptions"
            label="노출여부"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card v-if="!isHq" rounded="lg" elevation="1" class="mb-5 branch-product-tabs">
      <v-tabs v-model="branchProductTab" color="primary" @update:model-value="handleBranchTabChange">
        <v-tab value="REGISTERED">지점 등록 상품</v-tab>
        <v-tab value="HQ_AVAILABLE">본사 등록 상품</v-tab>
      </v-tabs>
      <v-card-text class="text-caption text-medium-emphasis py-3">
        <template v-if="branchProductTab === 'REGISTERED'">
          현재 지점에 설정된 상품입니다. 재고수량은 재고관리에서 입고확정된 수량으로 반영되며, 상품관리에서는 임가공비와 입금계좌만 수정합니다.
        </template>
        <template v-else>
          본사에 등록되어 있지만 현재 지점에는 아직 설정되지 않은 상품입니다. 재고수량은 본사 불출 후 지점 입고확정으로만 증가합니다.
        </template>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title class="d-flex align-center ga-2 py-4">
        <span class="text-subtitle-1 font-weight-bold">상품 목록</span>
        <v-chip size="small" color="primary" variant="tonal">{{ total }}건</v-chip>
      </v-card-title>
      <!-- 선택한 상품의 공급사 일괄 변경(본사) -->
      <div v-if="isHq" class="bulk-toolbar">
        <span class="text-body-2">선택 <strong>{{ selectedCodes.length }}</strong>개</span>
        <v-select
          v-model="bulkSellerCode"
          :items="sellerChoiceOptions"
          label="공급사로 바꾸기"
          variant="outlined"
          density="compact"
          hide-details
          class="bulk-seller-select"
        />
        <v-btn
          color="primary"
          variant="tonal"
          :disabled="!selectedCodes.length || !bulkSellerCode || bulkSaving"
          :loading="bulkSaving"
          @click="applyBulkSeller"
        >
          공급사 변경
        </v-btn>
        <v-btn v-if="selectedCodes.length" variant="text" size="small" @click="selectedCodes = []">선택 해제</v-btn>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th v-if="isHq" class="select-cell" @click.stop>
                <input type="checkbox" :checked="allPageSelected" aria-label="이 쪽 전체 선택" @change="toggleSelectAll" />
              </th>
              <th>이미지</th>
              <th>상품명</th>
              <th>카테고리</th>
              <th>브랜드</th>
              <th>공급사</th>
              <th>상품상태</th>
              <th>재고상태</th>
              <th>재고수량</th>
              <th>라벨</th>
              <th>할인</th>
              <th>기존가</th>
              <th>중량</th>
              <th>순도</th>
              <th>정렬</th>
              <th>노출</th>
              <th>등록일</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="columnCount" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" />
              </td>
            </tr>
            <tr v-else-if="!items.length">
              <td :colspan="columnCount" class="text-center py-8 text-medium-emphasis">등록된 상품이 없습니다.</td>
            </tr>
            <tr v-for="item in items" v-else :key="item.productCode" @click="goToDetail(item)">
              <td v-if="isHq" class="select-cell" @click.stop>
                <input type="checkbox" :checked="isSelected(item)" :aria-label="`${item.productName} 선택`" @change="toggleSelect(item)" />
              </td>
              <td><ProductThumbnail :object-key="item.thumbnailImageUrl" size="56" /></td>
            <td>
              <div class="font-weight-bold">{{ item.productName }}</div>
            </td>
              <td>{{ item.categoryName || '-' }}</td>
              <td>{{ item.brandName || '-' }}</td>
              <td>
                <span v-if="item.sellerName">{{ item.sellerName }}</span>
                <v-chip v-else size="x-small" color="warning" variant="tonal">미지정</v-chip>
              </td>
              <td><v-chip size="small" variant="tonal">{{ statusText(item.productStatus) }}</v-chip></td>
              <td><v-chip size="small" variant="tonal">{{ stockText(item.stockStatus) }}</v-chip></td>
              <td @click.stop>
                <div v-if="!isHq && !item.branchProductCode" class="d-flex align-center ga-2">
                  <v-chip size="small" color="warning" variant="tonal">미등록</v-chip>
                  <v-btn size="x-small" color="primary" variant="tonal" @click="openRegisterDialog(item)">
                    지점 설정 등록
                  </v-btn>
                </div>
                <span v-else>{{ item.stockQty ?? 0 }}</span>
              </td>
              <td>
                <div class="d-flex flex-wrap ga-1">
                  <v-chip
                    v-for="label in item.labels || []"
                    :key="label.productLabelCode"
                    size="x-small"
                    color="primary"
                    variant="tonal"
                  >
                    {{ label.labelName }}
                  </v-chip>
                  <span v-if="!(item.labels || []).length" class="text-caption text-medium-emphasis">-</span>
                </div>
              </td>
              <td>{{ discountText(item) }}</td>
              <td>{{ formatMoney(item.originalPrice) }}</td>
              <td>{{ item.weight || '-' }}</td>
              <td>{{ item.purity || '-' }}</td>
              <td>{{ item.orderby ?? '-' }}</td>
              <td @click.stop>
                <v-switch
                  :model-value="item.viewYn === 'Y'"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!isHq || togglingSet.has(item.productCode)"
                  :loading="togglingSet.has(item.productCode)"
                  @update:model-value="handleToggleView(item)"
                />
              </td>
              <td>{{ formatDate(item.insertDt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        :page="page"
        :size="size"
        :total="total"
        @update:page="onPageChange"
        @update:size="onSizeChange"
      />
    </v-card>

    <v-dialog v-model="registerDialog" max-width="520">
      <v-card class="branch-register-dialog">
        <v-card-title class="text-h6 font-weight-bold">{{ registerDialogTitle }}</v-card-title>
        <v-card-text>
          <div class="mb-4">
            <div class="text-caption text-medium-emphasis">상품</div>
            <div class="text-subtitle-1 font-weight-bold">{{ registerTarget?.productName || '-' }}</div>
            <div class="text-caption text-medium-emphasis">{{ registerTarget?.productCode }}</div>
          </div>
          <div class="hq-default-box mb-4">
            <div class="text-caption text-medium-emphasis mb-2">본사 기준 설정</div>
            <div class="default-row">
              <span>임가공비</span>
              <strong>{{ formatMoney(registerTarget?.hqProcessingFee) }}원</strong>
            </div>
            <div class="default-row">
              <span>입금계좌</span>
              <strong>{{ registerTarget?.hqBranchAccountLabel || '미설정' }}</strong>
            </div>
            <div class="text-caption text-medium-emphasis mt-2">
              지점 계좌를 선택하지 않으면 상품의 본사 기준 계좌가 주문에 적용됩니다.
            </div>
          </div>
          <div class="dialog-form-grid single">
            <v-text-field
              v-model.number="registerForm.processingFee"
              type="number"
              min="0"
              label="임가공비"
              variant="outlined"
              density="compact"
            />
          </div>
          <v-select
            v-model="registerForm.branchAccountCode"
            :items="cashAccountOptions"
            label="지점 입금계좌"
            variant="outlined"
            density="compact"
            clearable
          />
          <div class="text-caption text-medium-emphasis">
            현재 로그인한 지점의 임가공비와 입금계좌로 등록됩니다. 재고수량은 재고관리의 입고확정으로만 증가합니다.
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="registerDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="registering" @click="handleRegisterBranchProduct">{{ registerDialogActionText }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.bulk-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px 12px;
}

.bulk-seller-select {
  max-width: 220px;
}

.select-cell {
  width: 40px;
  text-align: center;
}

.search-row {
  display: grid;
  grid-template-columns: minmax(260px, 1.4fr) repeat(4, minmax(140px, 0.7fr)) auto auto;
  gap: 12px;
  align-items: center;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1320px;
}

.data-table th,
.data-table td {
  padding: 13px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  text-align: left;
  vertical-align: middle;
}

.data-table td {
  color: rgba(255, 255, 255, 0.88);
}

.data-table th {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.04);
}

.data-table .text-medium-emphasis {
  color: rgba(255, 255, 255, 0.62) !important;
}

.data-table tbody tr {
  cursor: pointer;
}

.data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.04);
}

.branch-register-dialog {
  color: rgba(255, 255, 255, 0.92);
}

.branch-register-dialog :deep(.text-medium-emphasis) {
  color: rgba(255, 255, 255, 0.68) !important;
}

.branch-register-dialog :deep(.v-field-label),
.branch-register-dialog :deep(.v-label) {
  color: rgba(255, 255, 255, 0.72) !important;
  opacity: 1;
}

.branch-register-dialog :deep(input) {
  color: rgba(255, 255, 255, 0.92) !important;
}

.dialog-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.dialog-form-grid.single {
  grid-template-columns: minmax(0, 1fr);
}

.hq-default-box {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  padding: 14px;
}

.default-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
}

.default-row + .default-row {
  margin-top: 8px;
}

.default-row span {
  color: rgba(255, 255, 255, 0.64);
  flex: 0 0 72px;
}

.default-row strong {
  color: rgba(255, 255, 255, 0.92);
  text-align: right;
  word-break: keep-all;
}

@media (max-width: 1200px) {
  .search-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
